import Sizzle from 'sizzle'
import { useCallback } from 'react'
import { Chat } from './chatSettingsConfig'
import * as parsers from './messageParserHelper'
import api from '../api/chat'
import chatLog from './chat-log'
import { useChatDispatch, useChatState } from '../state/ChatState'
import { useSaveUnreadMessageToCache } from '../hooks/useUnreadMessageCache'
import useJoinGroupByInvitation from '../hooks/useJoinGroupByInvitation'

/**
 * As other people go online/offline, capture their presence
 * availablity to display.
 */
const captureOthersPresence = ({ result, dispatch }) => {
  const data = parsers.isPersonOnline({ presence: result })
  dispatch({ type: 'PERSON_PRESENCE', data })
}

/*
  This monitors for presence notifications and will update the ChatState.
  Example responses:
  <presence xmlns='jabber:client' to='robinson@jabber.rootstechchat.org' from='user6@jabber.rootstechchat.org' type='subscribe'/>
  etc
*/
export const useOnPresenceHandler = () => {
  const { connection, refetchRoster } = useChatState()
  const dispatch = useChatDispatch()

  const onPresence = useCallback(
    (stanza) => {
      const doAutoAccept = Chat.Settings.AUTO_ACCEPT_ROSTER_INVITES

      const result = parsers.parsePresenceNotification({ stanza })
      const wantsToAddMe = result?.type === 'subscribe'

      if (doAutoAccept && wantsToAddMe) {
        chatLog(`[Presence] Someone wants to add me to their roster: ${result.jid}`)
        api.roster.autoAcceptInvite({ jid: result.jid, connection, refetchRoster })
      } else {
        const from = stanza.getAttribute('from')

        if (from && from.indexOf(Chat.Settings.MUC_DOMAIN) > 0) {
          dispatch({ type: 'ROOMS_AUTO_JOIN_SUCCESS', from })
        } else {
          captureOthersPresence({ result, dispatch })
        }
      }

      // Keep the listener alive
      return true
    },
    [connection, dispatch, refetchRoster]
  )

  return [onPresence]
}

/*
  This monitors for message notifications and will update the ChatState.
  This could be used to show new incoming messages on the "My Connections" view.
*/
export const useOnMessageHandler = () => {
  const { user } = useChatState()
  const dispatch = useChatDispatch()
  const [saveUnreadMessageToCache] = useSaveUnreadMessageToCache()

  const onMessage = useCallback(
    (stanza) => {
      const messageData = parsers.parseMessageChatNotification({ stanza })

      const isFromMe = user.authzid === messageData.fromJid
      const hasRightType = messageData.type === 'chat' || messageData.type === 'groupchat'
      if (hasRightType && messageData.message && !isFromMe) {
        dispatch({ type: 'UNREAD_MESSAGE', fromJid: messageData.fromJid })
        saveUnreadMessageToCache({ jid: messageData.fromJid })
        chatLog('[Messages] Received chat: ', messageData?.message)

        // Evaluate if we need to add this person to our roster
        dispatch({ type: 'EVALUATE_ADD_PERSON', fromJid: messageData.fromJid })
      }

      // Keep the listener alive
      return true
    },
    [dispatch, saveUnreadMessageToCache, user.authzid]
  )

  return [onMessage]
}

/*
  Handle direct invitations received to join a private chat room.
  Example:
    <message from="..." to="..." xml:lang="en" xmlns="jabber:client">
		<x jid="<guid>@<domain_url>" reason="..." xmlns="jabber:x:conference">
			<impromptu xmlns="http://rootstech.org/impromptu"/>
		</x>
	</message>
*/
export const useOnInvitationHandler = () => {
  const [joinGroupByInvitation] = useJoinGroupByInvitation()
  const dispatch = useChatDispatch()

  const onInvitation = useCallback(
    (stanza) => {
      const doAutoAccept = Chat.Settings.AUTO_ACCEPT_PRIVATE_CHAT_INVITES
      const chatRoom = Sizzle('message x', stanza)
        ?.pop()
        ?.getAttribute('jid')
      const isImpromptu = !!parsers.parseForImpromptu(stanza)

      if (doAutoAccept && isImpromptu && chatRoom) {
        joinGroupByInvitation(chatRoom)

        // Be sure to add this new message to the unread messages list.
        dispatch({ type: 'UNREAD_MESSAGE', fromJid: chatRoom })
      }

      // Keep the listener alive
      return true
    },
    [dispatch, joinGroupByInvitation]
  )

  return [onInvitation]
}

/*
  Bookmarks are used to track which group chats the person has joined.
  If the autoJoin flag is true, then in order to receive new messages, we
  need to declare our presence.
*/
export const useAutoJoinRooms = () => {
  const { connection, bookmarks, user } = useChatState()

  const doAutoJoin = useCallback(() => {
    bookmarks
      ?.filter((bookmark) => bookmark.autoJoin)
      .forEach((bookmark) => {
        setTimeout(() => {
          api.groups.sendAutoJoinPresence({
            connection,
            fromJid: user.authzid,
            chatRoom: bookmark.jid,
          })
        }, 750)
      })
  }, [bookmarks, connection, user.authzid])

  return [doAutoJoin]
}
